"use client";

import React from "react";
import { Feature } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import Image from "next/image";
import Link from "next/link";
import { renderRichText } from "@/lib/richText";
// import { logger } from '@/lib/logger';

interface FeatureProps {
  blok?: Feature & SbBlokData;
}

const FeatureComponent: React.FC<FeatureProps> = ({ blok }) => {
  if (!blok) return null;
  // logger.log(`blok :` + JSON.stringify(blok, null, 2));

  const {
    bg_style,
    intro_title,
    title,
    description,
    content_type,
    image,
    image_width,
    image_height,
    image_alt = "Image",
    video_details,
    image_position = "left",
    cta_button,
    cta_text,
    cta_link,
    icon_list,
    use_bottom_line,
  } = blok;

  const videoDetail = video_details?.[0];

  const renderMedia = () => {
    if (content_type === "video" && videoDetail && videoDetail.tag) {
      const videoSchema = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": videoDetail.name || title || "",
        "description": videoDetail.description || "",
        "thumbnailUrl": `https://img.youtube.com/vi/${videoDetail.tag}/maxresdefault.jpg`,
        "uploadDate": videoDetail.uploadDate || new Date().toISOString(),
        "duration": videoDetail.duration || "PT0M0S",
        "publisher": {
          "@type": "Organization",
          "name": "TargetDefense",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.targetdefense.com/logo.svg"
          }
        },
        "contentUrl": `https://www.youtube.com/watch?v=${videoDetail.tag}`,
        "embedUrl": `https://www.youtube.com/embed/${videoDetail.tag}`
      };

      return (
        <>
          <script type="application/ld+json">
            {JSON.stringify(videoSchema)}
          </script>
          <div className="video-responsive w-100 rounded-3 shadow overflow-hidden">
            <div className="ratio ratio-16x9">
              <iframe
                src={`https://www.youtube.com/embed/${videoDetail.tag}?iv_load_policy=3&modestbranding=1&playsinline=1&showinfo=0&rel=0&enablejsapi=1&controls=0`}
                title={videoDetail.name || "YouTube video player"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </>
      );
    } else if (image?.filename) {
      return (
        <Image
          src={image.filename}
          alt={image_alt}
          title={title || ""}
          className={
            !image_width || !image_height ? "img-cover h-100 w-100" : undefined
          }
          width={Number(image_width) || 100}
          height={Number(image_height) || 100}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          loading="lazy"
          quality={75}
        />
      );
    }
    return null;
  };

  const getListClass = () => {
    switch (blok.bullet_style) {
      case "target":
        return "feature_bullet_target";
      case "checkmark":
        return "feature_bullet_checkmark";
      default:
        return "list-checked";
    }
  };

  // logger.log("title color: " + blok.title_color);
  const contentSection = (
    <>
      {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
      {title && (
        <h2
          className={`mb-4 ${blok.title_color ? "text-" + blok.title_color : "text-primary"}`}
        >
          {title}
        </h2>
      )}
      {description && (
        <div
          className={`feature-description ${getListClass()}`}
          dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
        />
      )}
      {cta_button && cta_text && cta_link?.url && (
        <Link
          href={cta_link.url}
          target={
            (cta_link.linktype as string) === "external" ? "_blank" : "_self"
          }
          rel={
            (cta_link.linktype as string) === "external"
              ? "noopener noreferrer"
              : ""
          }
          className="button button-large button-primary text-white js-cta-btn"
        >
          {cta_text}
        </Link>
      )}
    </>
  );

  if (content_type === "icons") {
    return (
      <>
        <div
          {...storyblokEditable(blok)}
          className="container text-center text-md-start py-4 py-md-5 feature_content_icons"
        >
          <div className="row align-items-md-center justify-content-xl-center text-center text-md-start gy-3 gy-md-0">
            <div className="col-md col-xxl-20 text-center">
              {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
              {title && (
                <h2
                  className={`${blok.title_color ? "text-" + blok.title_color : "text-primary"}`}
                >
                  {title}
                </h2>
              )}
              {description && (
                <div className="feature-description"
                  dangerouslySetInnerHTML={{
                    __html: renderRichText(description),
                  }}
                />
              )}
              {icon_list &&
                icon_list.length > 0 &&
                (icon_list.some((icon) => icon.title) ? (
                  <div className="usps usps-four mt-5">
                    {icon_list.map((icon, index) => (
                      <div className="usp" key={icon.id || index}>
                        {icon.filename && (
                          <Image
                            src={icon.filename}
                            alt={icon.alt || ""}
                            title={icon.name || ""}
                            className="mb-3 js-lazy-load"
                            data-src={icon.filename}
                            width="70"
                            height="70"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            loading="lazy"
                            quality={75}
                          />
                        )}
                        {icon.name && <h3>{icon.name}</h3>}
                        {icon.title && <p>{icon.title}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="usps usps-four mt-5">
                    {icon_list.map((icon, index) => (
                      <div className="usp" key={icon.id || index}>
                        {icon.filename && (
                          <Image
                            src={icon.filename}
                            alt={icon.alt || icon.name || ""}
                            width={70}
                            height={70}
                            className="mx-auto d-inline-block mb-2 mb-lg-3"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            loading="lazy"
                            quality={75}
                          />
                        )}
                        <p className="mb-0 text-body">{icon.name}</p>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        </div>
        {use_bottom_line && <hr />}
      </>
    );
  }

  if (image_position === "full") {
    return (
      <>
        <section
          className="container py-5 py-md-5"
          {...(blok && storyblokEditable(blok))}
        >
          {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
          {title && (
            <h2
              className={`text-center ${blok.title_color ? "text-" + blok.title_color : "text-primary"}`}
            >
              {title}
            </h2>
          )}
          <div>
            {description && (
              <div
                className="margin-bottom-medium text-center feature-description"
                dangerouslySetInnerHTML={{
                  __html: renderRichText(description),
                }}
              />
            )}
            {cta_button && cta_text && cta_link?.url && (
              <div className="text-center">
                <Link
                  href={cta_link.url}
                  target={
                    (cta_link.linktype as string) === "external"
                      ? "_blank"
                      : "_self"
                  }
                  rel={
                    (cta_link.linktype as string) === "external"
                      ? "noopener noreferrer"
                      : ""
                  }
                  className="button button-large button-primary text-white js-cta-button"
                >
                  {cta_text}
                </Link>
              </div>
            )}
          </div>
        </section>
        {use_bottom_line && <hr />}
      </>
    );
  }

  if (image_position === "full_left") {
    return (
      <>
        <section
          className="container py-4 py-lg-5"
          {...(blok && storyblokEditable(blok))}
        >
          <div>
            {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
            {title && (
              <h2
                className={`${blok.title_color ? "text-" + blok.title_color : "text-primary"}`}
              >
                {title}
              </h2>
            )}
            {description && (
              <div className="feature-description"
                dangerouslySetInnerHTML={{
                  __html: renderRichText(description),
                }}
              />
            )}
            {cta_button && cta_text && cta_link?.url && (
              <Link
                href={cta_link.url}
                target={
                  (cta_link.linktype as string) === "external"
                    ? "_blank"
                    : "_self"
                }
                rel={
                  (cta_link.linktype as string) === "external"
                    ? "noopener noreferrer"
                    : ""
                }
                className="button button-large button-primary text-white js-cta-button"
              >
                {cta_text}
              </Link>
            )}
          </div>
        </section>
        {use_bottom_line && <hr />}
      </>
    );
  }

  if (image_position === "blue_title") {
    return (
      <>
        <div
          className="muted-section"
          {...storyblokEditable(blok)}
        >
          <div className="container">
            <div className="padding-medium">
              <div className="two-thirds-centered">
                {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
                {title && (
                  <h2
                    className={`text-center ${blok.title_color ? "text-" + blok.title_color : "text-primary"}`}
                  >{title}</h2>
                )}
                {description && (
                  <div
                    className="margin-bottom-medium feature-description"
                    dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
                  />
                )}
                {cta_button && cta_text && cta_link?.url && (
                  <div className="text-center">
                    <Link
                      href={cta_link.url}
                      target={(cta_link.linktype as string) === "external" ? "_blank" : "_self"}
                      rel={(cta_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
                      className="button button-large button-primary text-white js-cta-button"
                    >
                      {cta_text}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {use_bottom_line && <hr />}
      </>
    );
  }

  if (image_position === "report_left") {
    return (
      <>
        <div className="feature_report bg-light component-image-content">
          <div className="container row g-md-3 g-lg-3 align-items-center py-4 py-md-5 mx-auto">
            <div className="col-24 col-xl-10 mb-4 mb-xl-0 text-center">
              {renderMedia()}
              {cta_button && cta_link?.url && (
                <p>
                  <Link
                    href={cta_link.url}
                    className="button button-large button-primary mt-4"
                  >
                    Download now
                  </Link>
                </p>
              )}
            </div>
            <div className="col-24 col-xl-13 lead offset-xl-1">
              {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
              {title && <h2>{title}</h2>}
              {description && (
                <div
                  className="mb-3"
                  dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
                />
              )}
              {icon_list && icon_list.length > 0 && (
                <ul className="list-targets mb-0">
                  {icon_list.map((item, index) => (
                    <li key={item.id || index}>
                      {item.title}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (image_position === "report_right") {
    return (
      <>
        <div className="feature_report container py-4 py-md-5" {...storyblokEditable(blok)}>
          <div className="row">
            <div className="col-md-12 col-lg-13 col-xl-14 col-xxl-16">
              {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
              {title && <h2>{title}</h2>}
              {description && (
                <div
                  className="feature-description"
                  dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
                />
              )}
              {icon_list && icon_list.length > 0 && (
                <ul className="list-targets">
                  {icon_list.map((item, index) => (
                    <li key={item.id || index}>
                      {item.title}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="col text-center">
              {renderMedia()}
              {cta_button && cta_link?.url && (
                <p>
                  <Link
                    href={cta_link.url}
                    className="button button-large button-primary mt-4"
                  >
                    Download now
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }


  return bg_style === "dark" ? (
    <>
      <div className="bg-light">
        <div
          {...(blok && storyblokEditable(blok))}
          className="container feature_content"
        >
          <section className="py-10 py-md-5">
            <div className="row align-items-md-center justify-content-xl-center gy-3 gy-md-0">
              {image_position === "center" ? (
                <div className="row align-items-md-center justify-content-xl-center text-center text-md-start gy-3 gy-md-0">
                  <div className="col-md col-lg-11 col-xxl-10 mb-5">
                    {renderMedia()}
                  </div>
                  <div className="col-md col-lg-12 offset-xl-1 col-xl-13 col-xxl-12">
                    {contentSection}
                  </div>
                </div>
              ) : (
                <div className="container row g-md-3 g-lg-3 align-items-center mx-auto">
                  {image_position === "right" ? (
                    <>
                      <div className="col-24 col-lg-11 col-xxl-12">
                        {contentSection}
                      </div>
                      <div className="col-24 col-lg-11 col-xxl-10 offset-lg-1 mb-4 mb-lg-0 order-lg-last">
                        {renderMedia()}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="col-24 col-xl-10 mb-4 mb-xl-0 text-center">
                        {renderMedia()}
                      </div>
                      <div className="col-24 col-xl-13 lead offset-xl-1">
                        {contentSection}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
      {use_bottom_line && <hr />}
    </>
  ) : (
    <>
      <div
        {...(blok && storyblokEditable(blok))}
        className="container feature_content"
      >
        <section className="py-10 py-md-5">
          <div className="row align-items-md-center justify-content-xl-center gy-3 gy-md-0">
            {image_position === "center" ? (
              <div className="row align-items-md-center justify-content-xl-center text-center text-md-start gy-3 gy-md-0">
                <div className="col-md col-lg-11 col-xxl-10 mb-5">
                  {renderMedia()}
                </div>
                <div className="col-md col-lg-12 offset-xl-1 col-xl-13 col-xxl-12">
                  {contentSection}
                </div>
              </div>
            ) : (
              <div className="container row g-md-3 g-lg-3 align-items-center mx-auto">
                {image_position === "right" ? (
                  <>
                    <div className="col-24 col-lg-14">{contentSection}</div>
                    <div className="col-24 col-lg-9 offset-lg-1 mb-4 mb-lg-0 order-lg-last text-center">
                      {renderMedia()}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-24 col-lg-9 mb-4 mb-lg-0 text-center">
                      {renderMedia()}
                    </div>
                    <div className="col-24 col-lg-14 lead offset-lg-1">
                      {contentSection}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
      {use_bottom_line && <hr />}
    </>
  );
};

export default FeatureComponent;
